import { Link } from '@tanstack/react-router';
import { CheckIcon, CloudIcon, Link2, LogsIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { useIntegrations } from '@/hooks/useIntegrations';

interface Props {
  defaultProjectId: string;
}

export const Integrations = ({ defaultProjectId }: Props): JSX.Element => {
  const {
    noData,
    awsCredentials,
    awsRoles,
    datadogApiCredentials,
    gcpServiceAccounts,
  } = useIntegrations(defaultProjectId);

  if (noData !== true) {
    return <></>;
  }

  return (
    <div className="w-full max-w-2xl bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6">
      <div className="text-center mb-4">
        <h2 className="text-2xl font-medium text-gray-900 dark:text-white mb-2">
          <FormattedMessage id="rca.emptyStates.connectIntegrations.title" />
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          <FormattedMessage id="rca.emptyStates.connectIntegrations.description" />
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Link to="/integrations/aws">
          <Button variant="outline" size="sm" className="w-full">
            {[...awsCredentials, ...awsRoles].filter(
              ({ status }) => status === 'active',
            ).length === 0 ? (
              <CloudIcon className="w-4 h-4 mr-1" />
            ) : (
              <CheckIcon
                className="w-4 h-4 mr-1 text-green-500 stroke-10"
                strokeWidth={3}
              />
            )}
            <FormattedMessage id="rca.emptyStates.connectIntegrations.aws" />
          </Button>
        </Link>
        <Link to="/integrations/gcp">
          <Button variant="outline" size="sm" className="w-full">
            {gcpServiceAccounts.filter(({ status }) => status === 'active')
              .length === 0 ? (
              <CloudIcon className="w-4 h-4 mr-1" />
            ) : (
              <CheckIcon
                className="w-4 h-4 mr-1 text-green-500 stroke-10"
                strokeWidth={3}
              />
            )}
            <FormattedMessage id="rca.emptyStates.connectIntegrations.gcp" />
          </Button>
        </Link>
        <Link to="/integrations/monitoring/datadog-api-credentials">
          <Button variant="outline" size="sm" className="w-full">
            {datadogApiCredentials.filter(({ status }) => status === 'active')
              .length === 0 ? (
              <LogsIcon className="w-4 h-4 mr-1" />
            ) : (
              <CheckIcon
                className="w-4 h-4 mr-1 text-green-500 stroke-10"
                strokeWidth={3}
              />
            )}
            <FormattedMessage id="rca.emptyStates.connectIntegrations.datadog" />
          </Button>
        </Link>
        <Link to="/integrations">
          <Button variant="outline" size="sm" className="w-full">
            <Link2 className="w-4 h-4 mr-1" />
            <FormattedMessage id="rca.emptyStates.connectIntegrations.seeAll" />
          </Button>
        </Link>
      </div>
    </div>
  );
};
